import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Handshake,
  Heart,
  Award,
  Layers,
  Compass,
  Lightbulb,
  Leaf,
  Globe,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Container, Badge } from '../components/ui';
import { cn } from '../utils/cn';
import {
  PageHero,
  ServiceCard,
  OurPurpose,
  WhyChooseUsSection,
  ClientLogoCarousel,
  IstaLabsTeaserSection,
} from '../components/sections';
import { WHAT_WE_DO_SERVICES, WHAT_WE_DO_MEDIA } from '../data/whatWeDo';

/*
 * About
 * -----
 * The /about page. Rebuilt around the client brief:
 *   1. People-first hero
 *   2. "Our story" — Istatec-specific narrative (mid-scale, India-based,
 *      innovation-first) with real numbers only
 *   3. Stats row (15+ clients, 2+ countries, India HQ)
 *   4. "We have worked with" — right-to-left auto-scrolling logo
 *      carousel of past client organisations (Clearbit logos w/
 *      per-tile text fallback if the CDN can't resolve one)
 *   5. "See all our services" — reuses the shared `ServiceCard` grid
 *      so this page stays in perfect sync with /services
 *
 * Explicitly removed vs. the prior version:
 *   - Growing Together timeline (user asked to drop it)
 *   - Global Presence section (contained invented numbers +
 *     imaginary global offices)
 *   - Mission / Vision / Values card blocks (superseded by the new
 *     Our-Story narrative + WhyChooseUsSection on /contact)
 */

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const About: React.FC = () => (
  <>
    <PageHero
      badge={
        <Badge className="mb-6 bg-white/10 text-white border border-white/20">
          About Us
        </Badge>
      }
      title="People at the heart of every experience"
      subtitle="Where expert, human-centered thinking transforms businesses and accelerates growth. Everything we do begins with people — our team and our clients."
    />

    <OurStorySection />
    {/* "Our purpose is simple yet powerful" — the philosophical
        pause between the story and the numbers. Same component
        that also lives on the Contact page (below the form). */}
    <OurPurpose />
    {/* "Our values" — 4 principle statements shown in a full-bleed
        auto-rotating slideshow with cover video backgrounds and a
        text overlay per slide. Sits between our external-facing
        purpose statement and the concrete "What sets us apart"
        differentiators so the flow reads: what we exist for →
        what we believe → what makes us different in practice. */}
    <OurValuesSection />
    {/* "What sets us apart" — 7 values-oriented differentiators
        modeled on the Zensar reference (icon + heading + short
        description + divider between rows). */}
    <WhatSetsUsApartSection />
    <StatsSection />
    <ClientLogoCarousel />
    <SeeAllServicesSection />
    {/* IstaLabs teaser — same shared component the homepage uses at
        the bottom. On the About page it sits between "See all our
        services" and the final "Why teams choose Istatec" pitch,
        giving visitors a clear route into our innovation practice
        after they've scrolled through what we ship day-to-day. */}
    <IstaLabsTeaserSection />
    {/* "Why teams choose Istatec" — same 8-tile grid we render on
        the Contact page (below the form). Placed as the closing
        pitch on the About page: after the visitor has learned who
        we are, what we stand for, and what we do, this is the
        practical answer to "so what does engaging actually look
        like?" It sits directly above the global <ConnectSection/>
        rendered by Layout.tsx, so the flow reads:
          reasons to pick us → contact form (from Layout) → footer. */}
    <WhyChooseUsSection />
  </>
);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * "Our story" — split layout: header centered at the top, three-
 * paragraph narrative on the left, autoplaying cover video on the
 * right. Both columns slide into place from opposite edges when the
 * section scrolls into view — text slides in from the left, video
 * slides across the screen from the left toward its home column on
 * the right (per user direction), and the two columns "meet" as the
 * section becomes visible.
 *
 * On mobile the columns stack: text first, video below, both fading
 * up together.
 *
 * Uses `overflow-hidden` on the outer section so the video's off-
 * screen start position doesn't cause a temporary horizontal
 * scrollbar mid-animation.
 */
const OurStorySection: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Nudge autoplay on mount — some browsers freeze on the first
  // frame even with `autoPlay muted`.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="bg-white py-16 md:py-24 lg:py-28 overflow-hidden">
      <Container>
        {/* Centered header, above the two-column split */}
        <motion.h2
          className="heading-lg text-navy-900 text-center mb-10 md:mb-14"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          Our story
        </motion.h2>

        {/*
          Two-column grid with equal columns on md+ and comfortable
          gap. `items-center` centers the video vertically against
          the (usually taller) text column so the pair looks balanced.
        */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* ────────── Left: story paragraphs ──────────
              Slides in from the far left as the section enters view. */}
          <motion.div
            className="space-y-6 text-lg md:text-xl text-navy-700 leading-relaxed order-2 lg:order-1"
            initial={{ opacity: 0, x: -80 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p>
              Istatec is a mid-scale technology consultancy guided by a
              simple belief: technology only creates value when it&rsquo;s
              built around people. We put our clients at the center of every
              engagement and take ownership of the outcomes — because
              meaningful experiences, not shipped code, are what shape great
              work.
            </p>
            <p>
              Headquartered in India, we work with 15+ organizations across
              2+ countries — a deliberately mid-scale team that pairs senior
              technical advisors with best-sourced expert engineers. Every
              project reflects our commitment to real value, honest
              collaboration, and putting people first.
            </p>
            <p>
              We innovate and iterate every day — new frameworks, models,
              and patterns worth adopting — because staying current is how
              we serve our clients better and how we scale what we deliver.
              That momentum is why the toolset a project ships with in
              month six is rarely the one we started with in month one.
            </p>
          </motion.div>

          {/* ────────── Right: cover video ──────────
              Video slides in horizontally from the LEFT edge of the
              screen toward its final position in the right column, per
              user direction. Starts at `x: -600` (well off to the left
              relative to its home) and settles into `x: 0`. The
              outer section has `overflow-hidden`, so the video is
              clipped during its journey and appears to "arrive" from
              across the section rather than causing horizontal
              scrollbar jitter. */}
          <motion.div
            className={
              'order-1 lg:order-2 relative w-full aspect-video ' +
              'rounded-2xl overflow-hidden bg-navy-900 ' +
              'shadow-[0_25px_60px_-15px_rgba(2,8,20,0.35)] ' +
              'ring-1 ring-navy-100'
            }
            initial={{ opacity: 0, x: -600 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <video
              ref={videoRef}
              src={WHAT_WE_DO_MEDIA.itConsulting}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

/**
 * "Our values" — 4 principle statements shown as a full-bleed
 * auto-rotating slideshow. Layout mirrors the reference (Zensar
 * About page): section heading in a Container above, then a
 * full-bleed video panel with the value name + description in a
 * left-anchored overlay. Bottom-right holds prev/next chevrons and
 * bottom-center holds dot indicators. Auto-advances every 7 seconds;
 * any manual navigation resets the timer.
 *
 * Video backgrounds are pulled from the shared WHAT_WE_DO_MEDIA
 * catalog — using cover clips we already load elsewhere on the site
 * so browsers cache them once and the About page benefits from that
 * cache warmth.
 */
interface Value {
  id: string;
  title: string;
  description: string;
  mediaUrl: string;
}

const VALUES: Value[] = [
  {
    id: 'client-first',
    title: 'Client at the center',
    description:
      "Every decision starts with what genuinely moves your business. We don\u2019t just deliver features and hand you the keys \u2014 we take ownership of the outcome and stay engaged through the results, not just the release.",
    mediaUrl: WHAT_WE_DO_MEDIA.whatWeDoOverview,
  },
  {
    id: 'ownership',
    title: 'Ownership by default',
    description:
      "Small, senior teams own the work end-to-end \u2014 from architecture calls to end-user support. No hand-offs, no dressed-up status reports, no relayed messages. You talk to the people actually building the thing.",
    mediaUrl: WHAT_WE_DO_MEDIA.itConsulting,
  },
  {
    id: 'curiosity',
    title: 'Curiosity as a discipline',
    description:
      "We keep learning as a habit, not a benefit. Every couple of weeks the team explores something new \u2014 a framework, a model, a pattern \u2014 and the useful ones make their way into how we build for the next client.",
    mediaUrl: WHAT_WE_DO_MEDIA.aiGenai,
  },
  {
    id: 'human',
    title: 'Human by default',
    description:
      "Technology is only useful when it\u2019s built around people. Every project, every review, every conversation \u2014 we lead with what makes the humans on both sides succeed, not what makes the deck look good.",
    mediaUrl: WHAT_WE_DO_MEDIA.applicationServices,
  },
];

// How long to hold each slide before auto-advancing. Long enough
// that a reader can absorb the copy, short enough that the loop
// feels alive.
const AUTOROTATE_MS = 7000;

const OurValuesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeValue = VALUES[activeIndex];

  // Auto-rotate the slideshow. Any manual navigation resets the
  // interval so users don't lose the slide they just jumped to.
  const [manualNudge, setManualNudge] = useState(0);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (reduceMotion) return;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % VALUES.length);
    }, AUTOROTATE_MS);
    return () => clearInterval(id);
  }, [manualNudge, reduceMotion]);

  const goToPrev = () => {
    setActiveIndex((i) => (i - 1 + VALUES.length) % VALUES.length);
    setManualNudge((n) => n + 1);
  };
  const goToNext = () => {
    setActiveIndex((i) => (i + 1) % VALUES.length);
    setManualNudge((n) => n + 1);
  };
  const goTo = (index: number) => {
    setActiveIndex(index);
    setManualNudge((n) => n + 1);
  };

  return (
    <section className="bg-white py-16 md:py-20 lg:py-24">
      <Container>
        <motion.h2
          className="heading-lg text-navy-900 mb-8 md:mb-10"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          Our values
        </motion.h2>
      </Container>

      {/* Full-bleed slideshow — sits outside the Container so it
          spans the entire viewport width (matches the reference). */}
      <div
        className="relative grid overflow-hidden bg-navy-950"
        role="region"
        aria-roledescription="carousel"
        aria-label="Our values"
      >
        <AnimatePresence mode="sync">
          <motion.div
            key={activeValue.id}
            className="relative col-start-1 row-start-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Background video for this slide. `key` on the wrapping
                motion.div re-mounts this element per slide, which is
                what triggers the cross-fade. */}
            <video
              key={activeValue.mediaUrl}
              src={activeValue.mediaUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden
            />

            {/* Left-anchored dark gradient so the copy always reads
                clearly regardless of what the video is showing in
                that frame. The gradient softens to transparent by
                the right edge so the video's imagery remains visible
                on the right half of the slide. */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"
            />

            {/* Text overlay — vertically centered, left-anchored
                inside the shared Container so the copy lines up with
                the section heading above. */}
            <div className="relative flex items-center min-h-[420px] md:min-h-[520px] py-16 pb-28">
              <Container>
                <div className="max-w-xl md:max-w-2xl">
                  <motion.h3
                    className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4 md:mb-5"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.2,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {activeValue.title}
                  </motion.h3>
                  <motion.p
                    className="text-base md:text-lg lg:text-xl text-white/85 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.3,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {activeValue.description}
                  </motion.p>
                </div>
              </Container>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Controls — glassy chevrons at the bottom-right, matching
            the reference. z-10 keeps them above the video overlay. */}
        <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 flex items-center gap-3 z-10">
          <CarouselButton
            onClick={goToPrev}
            aria-label="Previous value"
          >
            <ChevronLeft className="w-5 h-5" />
          </CarouselButton>
          <CarouselButton onClick={goToNext} aria-label="Next value">
            <ChevronRight className="w-5 h-5" />
          </CarouselButton>
        </div>

        {/* Dot indicators — bottom-center. Active dot stretches
            slightly wider so the current position is unmistakable. */}
        <div
          className="absolute bottom-8 md:bottom-10 left-6 md:left-1/2 md:-translate-x-1/2 flex items-center gap-2 z-10"
          role="tablist"
          aria-label="Value slides"
        >
          {VALUES.map((v, i) => (
            <button
              key={v.id}
              type="button"
              onClick={() => goTo(i)}
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Show value: ${v.title}`}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === activeIndex
                  ? 'bg-white w-10'
                  : 'bg-white/45 w-5 hover:bg-white/75',
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const CarouselButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  'aria-label': string;
}> = ({ onClick, children, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 hover:border-white/50 backdrop-blur-sm text-white transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900"
    {...rest}
  >
    {children}
  </button>
);

/**
 * "What sets us apart" — 7 values-oriented differentiators laid out
 * as a vertical list. Layout mirrors the reference the user shared:
 * icon in an accent chip on the left, bold heading + muted
 * description in the middle, thin divider between rows. Left-anchored
 * within a `max-w-5xl` column so the row length stays comfortable to
 * scan.
 *
 * Content is Istatec-specific (not the Zensar copy) — every row
 * anchors to something we've actually established elsewhere on the
 * site (senior engineers, mid-scale focus, >98% CSAT, continuous
 * R&D, direct client interaction, etc.).
 */
interface Differentiator {
  icon: React.ElementType;
  title: string;
  description: string;
}

const DIFFERENTIATORS: Differentiator[] = [
  {
    icon: Handshake,
    title: 'Enduring client relationships',
    description:
      'We invest in relationships that last \u2014 rooted in trust and the ability to consistently deliver value beyond the brief.',
  },
  {
    icon: Award,
    title: 'More than a vendor',
    description:
      'With top-quartile industry CSAT scores, we act as strategic partners \u2014 consultative, flexible, and committed to going the extra mile for our clients.',
  },
  {
    icon: Heart,
    title: 'Human-centered',
    description:
      'Our people-first culture prioritizes happiness, purpose, and balance \u2014 shaping how we think, work, and grow together.',
  },
  {
    icon: Layers,
    title: 'Our 3E approach',
    description:
      'Our 3Es \u2014 experience, engineering, and engagement \u2014 blend the creative depth of a design agency, the brand impact of a marketing powerhouse, and the scale of a modern tech firm to deliver transformative experiences.',
  },
  {
    icon: Compass,
    title: 'Domain-led transformation at scale',
    description:
      'Our strong domain knowledge is powered by dedicated industry service groups and service-line experts for targeted, effective solutions.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation in action',
    description:
      'IstaLabs, our innovation hub, transforms cutting-edge ideas into real-world impact through applied research, future-ready tech, and human-centered design.',
  },
  {
    icon: Leaf,
    title: 'Sustainability beyond ourselves',
    description:
      "We don\u2019t just embrace ESG internally \u2014 we help our clients embed sustainability into their own transformation journeys.",
  },
];

const WhatSetsUsApartSection: React.FC = () => (
  <section className="bg-white py-16 md:py-20 lg:py-24">
    <Container>
      <motion.h2
        className="heading-lg text-navy-900 mb-10 md:mb-14"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        What sets us apart
      </motion.h2>

      {/*
        Constrained width so the rows read like editorial content
        rather than stretching across the whole viewport. Border-top
        gives the first row a subtle upper edge; each subsequent row
        just gets a border-bottom (via the DifferentiatorRow), so we
        end up with clean hairlines between every entry and one at
        the very top.
      */}
      <div className="max-w-5xl border-t border-navy-100">
        {DIFFERENTIATORS.map((item, i) => (
          <DifferentiatorRow
            key={item.title}
            item={item}
            animationDelay={Math.min(i, 5) * 0.05}
          />
        ))}
      </div>
    </Container>
  </section>
);

const DifferentiatorRow: React.FC<{
  item: Differentiator;
  animationDelay: number;
}> = ({ item, animationDelay }) => {
  const Icon = item.icon;
  return (
    <motion.div
      className="flex items-start gap-4 md:gap-6 py-6 md:py-7 border-b border-navy-100"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{
        duration: 0.4,
        delay: animationDelay,
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      {/* Icon chip — accent-tinted so the rail reads as a clear
          left-edge marker without needing extra decoration. */}
      <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-lg bg-accent-50 text-accent-600 flex items-center justify-center">
        <Icon className="w-5 h-5 md:w-[22px] md:h-[22px]" aria-hidden />
      </div>

      {/* Heading + description */}
      <div className="flex-1 min-w-0">
        <h3 className="text-lg md:text-xl font-bold text-navy-900 mb-1.5">
          {item.title}
        </h3>
        <p className="text-[15px] md:text-base text-navy-600 leading-relaxed">
          {item.description}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Stats row — 15+ / 2+ / India. Same editorial `border-top-2` treatment
 * we use on the service detail pages so the "hard numbers" tiles
 * across the site look like they belong to one family.
 */
const StatsSection: React.FC = () => (
  <section className="bg-navy-50/60 py-14 md:py-20">
    <Container>
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <Stat number="15+" label="Happy clients we&rsquo;ve served" />
        <Stat number="2+" label="Countries served" />
        <Stat icon={Globe} label="Ready to ship globally" />
      </motion.div>
    </Container>
  </section>
);

/**
 * Single stat tile — either a big text "number" (e.g. "15+") or an
 * icon in its place. Optional-props pair lets callers pass whichever
 * fits: text numbers for quantitative stats, an icon for qualitative
 * "globally ready" style claims.
 */
const Stat: React.FC<{
  number?: string;
  icon?: React.ElementType;
  label: string;
}> = ({ number, icon: Icon, label }) => (
  <div className="border-t-2 border-navy-900 pt-5">
    {Icon ? (
      <Icon
        className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-navy-900 mb-3"
        strokeWidth={1.75}
        aria-hidden
      />
    ) : (
      <p className="text-4xl md:text-5xl lg:text-[56px] font-bold text-navy-900 mb-3 leading-none">
        {number}
      </p>
    )}
    <p
      className="text-sm md:text-[15px] text-navy-600 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: label ?? '' }}
    />
  </div>
);

// `<ClientLogoCarousel />` is now the shared export from
// `src/components/sections/ClientLogoCarousel.tsx` — used here and on
// the homepage so both surfaces stay perfectly in sync.

/**
 * "See all our services" — reuses the shared `ServiceCard` component
 * so this grid stays visually identical to the /services listing and
 * to the "Explore our other services" grid on the detail pages. All
 * 11 services in `WHAT_WE_DO_SERVICES` are rendered.
 */
const SeeAllServicesSection: React.FC = () => (
  <section className="bg-white py-16 md:py-20 lg:py-24">
    <div className="site-wide-container">
      <motion.h2
        className="heading-lg text-navy-900 mb-10 md:mb-14"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        See all our services
      </motion.h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
        {WHAT_WE_DO_SERVICES.map((s, i) => (
          <ServiceCard
            key={s.id}
            service={s}
            animationDelay={Math.min(i, 5) * 0.06}
          />
        ))}
      </div>
    </div>
  </section>
);
